import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

/**
 * @param {number} cents
 * @param {string} [currency]
 * @returns {string}
 */
function formatCartMoney(cents, currency = 'USD') {
  try {
    const formatted = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);

    return formatted.replace(/\.00$/, '');
  } catch {
    const amount = cents / 100;
    return amount % 1 === 0 ? `$${amount}` : `$${amount.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}`;
  }
}

/**
 * @param {Record<string, any>} item
 * @returns {boolean}
 */
function isSubscribedLineItem(item) {
  return Boolean(
    item.selling_plan_allocation ||
      item.selling_plan_id ||
      item.selling_plan
  );
}

/**
 * @param {Record<string, any>} item
 * @returns {number}
 */
function lineSubscribeSavings(item) {
  const quantity = Number(item.quantity) || 1;
  const finalLine = Number(item.final_line_price ?? item.line_price ?? 0);
  const unitFinal = Number(item.final_price ?? item.discounted_price ?? item.price ?? 0);
  const unitOriginal = Number(item.original_price ?? item.price ?? 0);
  const compareAt = Number(item.selling_plan_allocation?.compare_at_price ?? 0);
  const candidates = [
    compareAt > 0 ? compareAt * quantity - finalLine : 0,
    unitOriginal * quantity - finalLine,
    Number(item.original_line_price ?? 0) - finalLine,
    (unitOriginal - unitFinal) * quantity,
  ];

  return Math.max(0, ...candidates);
}

/**
 * @param {HTMLElement} container
 * @param {{ savings: number, compareTotal: number, show: boolean, currency?: string }} state
 */
function applySubscribeSavingsDisplay(container, state) {
  container.dataset.showSubscribeSavings = state.show && state.savings > 0 ? 'true' : 'false';
  container.dataset.subscribeSavingsCents = String(state.savings || 0);
  container.dataset.subscribeCompareTotalCents = String(state.compareTotal || 0);

  let savingsEl = container.querySelector('[data-cart-subscribe-savings]');
  let compareEl = container.querySelector('[data-cart-subscribe-compare-total]');

  if (!state.show || state.savings <= 0) {
    savingsEl?.classList.add('hidden');
    compareEl?.classList.add('hidden');
    return;
  }

  /*
  if (!savingsEl) {
    const prefix = container.dataset.subscribeSavingsPrefix || 'You saved';
    const suffix = container.dataset.subscribeSavingsSuffix || 'with subscribe';
    savingsEl = document.createElement('p');
    savingsEl.className = 'cart-totals__subscribe-savings';
    savingsEl.dataset.cartSubscribeSavings = '';
    savingsEl.innerHTML = `${prefix} <strong class="cart-totals__subscribe-savings-amount"></strong> ${suffix}`;
    container.querySelector('.cart-totals__total')?.after(savingsEl);
  }

  const amountEl = savingsEl?.querySelector('.cart-totals__subscribe-savings-amount');

  if (amountEl) {
    amountEl.textContent = formatCartMoney(state.savings, state.currency);
  }

  savingsEl?.classList.remove('hidden');
  */

  if (!compareEl) {
    const totalValues = container.querySelector('.cart-totals__total-values');
    compareEl = document.createElement('s');
    compareEl.className = 'cart-totals__total-compare cart-secondary-typography';
    compareEl.dataset.cartSubscribeCompareTotal = '';
    totalValues?.appendChild(compareEl);
  }

  compareEl.textContent = formatCartMoney(state.compareTotal, state.currency);
  compareEl.classList.remove('hidden');
}

/**
 * @returns {HTMLElement | null}
 */
function getSubscribeSavingsContainer() {
  return document.querySelector('.cart-drawer__summary [data-subscribe-savings-prefix]');
}

/**
 * @param {Record<string, any> | undefined} cart
 * @returns {{ savings: number, compareTotal: number, show: boolean, currency?: string }}
 */
function calculateSubscribeSavingsState(cart) {
  let savings = 0;
  let hasSubscribe = false;

  if (cart?.items) {
    for (const item of cart.items) {
      if (!isSubscribedLineItem(item)) continue;

      hasSubscribe = true;
      savings += lineSubscribeSavings(item);
    }
  }

  const cartTotal = Number(cart?.total_price ?? 0);

  return {
    savings,
    compareTotal: cartTotal + savings,
    show: hasSubscribe && savings > 0,
    currency: cart?.currency,
  };
}

/**
 * @returns {{ savings: number, compareTotal: number, show: boolean } | null}
 */
function readSubscribeSavingsFromDom() {
  const container = getSubscribeSavingsContainer();

  if (!container) return null;

  const show = container.dataset.showSubscribeSavings === 'true';
  const savings = Number(container.dataset.subscribeSavingsCents ?? 0);
  const compareTotal = Number(container.dataset.subscribeCompareTotalCents ?? 0);

  return { savings, compareTotal, show };
}

/**
 * @param {Record<string, any> | undefined} cart
 */
function updateSubscribeSavingsFromCart(cart) {
  const container = getSubscribeSavingsContainer();

  if (!container) return;

  const cartState = calculateSubscribeSavingsState(cart);

  if (cartState.show && cartState.savings > 0) {
    applySubscribeSavingsDisplay(container, cartState);
    return;
  }

  const domState = readSubscribeSavingsFromDom();

  if (domState?.show && domState.savings > 0) {
    applySubscribeSavingsDisplay(container, { ...domState, currency: cart?.currency });
    return;
  }

  applySubscribeSavingsDisplay(container, { ...cartState, show: false });
}

/**
 * @returns {Promise<void>}
 */
async function syncSubscribeSavingsAfterMorph() {
  const container = getSubscribeSavingsContainer();

  if (!container) return;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      credentials: 'same-origin',
    });

    if (!response.ok) {
      const domState = readSubscribeSavingsFromDom();

      if (domState) applySubscribeSavingsDisplay(container, domState);
      return;
    }

    const cart = await response.json();
    updateSubscribeSavingsFromCart(cart);
  } catch (error) {
    console.error('Cart subscribe savings:', error);
    const domState = readSubscribeSavingsFromDom();

    if (domState) applySubscribeSavingsDisplay(container, domState);
  }
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function parseCartLine(value) {
  const match = String(value ?? '').match(/\d+$/);
  const parsed = match ? parseInt(match[0], 10) : parseInt(String(value ?? ''), 10);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function parseSellingPlanId(value) {
  const parsed = parseInt(String(value ?? ''), 10);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * @param {string} [lineKey]
 * @param {number} fallbackLine
 * @returns {Promise<number>}
 */
async function resolveLiveCartLine(lineKey, fallbackLine) {
  if (!lineKey) return fallbackLine;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      credentials: 'same-origin',
    });

    if (!response.ok) return fallbackLine;

    const cart = await response.json();
    const index = cart.items.findIndex((item) => item.key === lineKey);

    return index >= 0 ? index + 1 : fallbackLine;
  } catch {
    return fallbackLine;
  }
}

/**
 * @param {HTMLElement} container
 * @returns {number}
 */
function resolveCartLineIndex(container) {
  let line = parseCartLine(container.dataset.lineIndex);
  if (line) return line;

  line = parseCartLine(container.dataset.cartLine);
  if (line) return line;

  const row = container.closest('.cart-items__table-row');
  const quantityInput = row?.querySelector('input[data-cart-line]');

  if (quantityInput instanceof HTMLInputElement) {
    line = parseCartLine(quantityInput.dataset.cartLine);
  }

  return line;
}

/**
 * @param {HTMLElement} container
 * @returns {number}
 */
function resolveCartQuantity(container) {
  let quantity = parseCartLine(container.dataset.quantity);
  if (quantity) return quantity;

  const row = container.closest('.cart-items__table-row');
  const quantityInput = row?.querySelector('input[data-cart-line]');

  if (quantityInput instanceof HTMLInputElement) {
    quantity = parseCartLine(quantityInput.value);
  }

  return quantity || 1;
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.push(component.dataset.sectionId);
    }
  });

  return sectionIds;
}

/**
 * @param {Record<string, any>} data
 * @param {boolean} ok
 * @returns {string}
 */
function getCartErrorMessage(data, ok) {
  if (ok && !data.errors) return '';

  if (typeof data.description === 'string' && data.description && data.description !== 'Cart Error') {
    return data.description;
  }

  if (typeof data.errors === 'string') return data.errors;

  if (typeof data.message === 'string' && data.message !== 'Cart Error') {
    return data.message;
  }

  return 'Unable to update subscription.';
}

/**
 * @returns {Promise<Record<string, any>>}
 */
async function fetchCartJson() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error('Unable to load cart.');
  }

  return response.json();
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} payload
 * @returns {Promise<Record<string, any>>}
 */
async function postCartJson(url, payload) {
  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
  const responseText = await response.text();
  /** @type {Record<string, any>} */
  let data = {};

  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch (error) {
      data = { errors: 'Cart update failed.' };
    }
  }

  const errorMessage = getCartErrorMessage(data, response.ok);

  if (errorMessage) {
    const error = new Error(errorMessage);
    /** @type {any} */ (error).status = response.status;
    /** @type {any} */ (error).data = data;
    throw error;
  }

  return data;
}

/**
 * @param {Record<string, any>} cart
 * @param {string} [lineKey]
 * @param {number} fallbackLine
 * @returns {number}
 */
function findCartLineIndex(cart, lineKey, fallbackLine) {
  if (lineKey) {
    const index = cart.items.findIndex((item) => item.key === lineKey);

    if (index >= 0) return index + 1;
  }

  return parseCartLine(fallbackLine);
}

/**
 * @param {string | null | undefined} sellingPlanId
 * @returns {number | null}
 */
function buildSellingPlanPayload(sellingPlanId) {
  const planId = sellingPlanId ? parseSellingPlanId(sellingPlanId) : 0;

  return planId > 0 ? planId : null;
}

/**
 * @param {Record<string, any>} item
 * @returns {Record<string, string>}
 */
function collectLineProperties(item) {
  /** @type {Record<string, string>} */
  const properties = {};
  const source = item?.properties;

  if (!source || typeof source !== 'object') return properties;

  for (const [key, value] of Object.entries(source)) {
    if (value !== null && value !== undefined && String(value) !== '') {
      properties[key] = String(value);
    }
  }

  return properties;
}

/**
 * @param {number} line
 * @param {number} quantity
 * @param {string | null} sellingPlanId
 * @param {string[]} sections
 * @returns {Promise<Record<string, any>>}
 */
async function changeCartLineSellingPlanDirect(line, quantity, sellingPlanId, sections) {
  /** @type {Record<string, unknown>} */
  const payload = {
    line,
    quantity,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  };

  const planId = buildSellingPlanPayload(sellingPlanId);

  if (planId) {
    payload.selling_plan = planId;
  } else if (quantity > 0) {
    payload.selling_plan = null;
  }

  return postCartJson(Theme.routes.cart_change_url, payload);
}

/**
 * Remove the line and re-add it with or without a selling plan.
 * Shopify (and subscription apps) often reject selling_plan updates on /cart/change.
 *
 * @param {string} lineKey
 * @param {number} fallbackLine
 * @param {string | null} sellingPlanId
 * @param {number} quantity
 * @param {string[]} sections
 * @returns {Promise<Record<string, any>>}
 */
async function changeCartLineSellingPlanViaReadd(
  lineKey,
  fallbackLine,
  sellingPlanId,
  quantity,
  sections
) {
  const cart = await fetchCartJson();
  const line = findCartLineIndex(cart, lineKey, fallbackLine);
  const item = cart.items[line - 1];

  if (!item) {
    throw new Error('Unable to find cart line.');
  }

  const qty = parseCartLine(quantity) || Number(item.quantity) || 1;
  const properties = collectLineProperties(item);

  await postCartJson(Theme.routes.cart_change_url, {
    line,
    quantity: 0,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });

  /** @type {Record<string, unknown>} */
  const addItem = {
    id: item.variant_id,
    quantity: qty,
  };

  if (Object.keys(properties).length) {
    addItem.properties = properties;
  }

  const planId = buildSellingPlanPayload(sellingPlanId);

  if (planId) {
    addItem.selling_plan = planId;
  }

  return postCartJson(Theme.routes.cart_add_url, {
    items: [addItem],
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });
}

/**
 * Shopify requires `line` (1-based index), not `id`, when changing selling_plan.
 *
 * @param {number} lineIndex
 * @param {string | null} sellingPlanId
 * @param {number} quantity
 * @param {string} [lineKey]
 * @returns {Promise<Record<string, any>>}
 */
async function changeCartLineSellingPlan(lineIndex, sellingPlanId, quantity, lineKey = '') {
  const sections = getCartSectionIds();
  const cart = await fetchCartJson();
  const line = findCartLineIndex(cart, lineKey, lineIndex);
  const qty = parseCartLine(quantity) || 1;

  if (!line) {
    throw new Error('Unable to find cart line.');
  }

  try {
    return await changeCartLineSellingPlanDirect(line, qty, sellingPlanId, sections);
  } catch (error) {
    const status = /** @type {any} */ (error)?.status;
    const message = error instanceof Error ? error.message : '';
    const shouldReadd =
      status === 422 || /selling.?plan|subscription|cannot apply|cart error/i.test(message);

    if (!shouldReadd) throw error;

    return changeCartLineSellingPlanViaReadd(lineKey, lineIndex, sellingPlanId, qty, sections);
  }
}

/** @type {ResizeObserver | undefined} */
let subscribeIndicatorResizeObserver;

/**
 * @param {HTMLElement} container
 */
function syncToggleStateFromInputs(container) {
  const subscribeInput = container.querySelector(
    '.cart-line-subscribe__option--subscribe .cart-subscribe-toggle'
  );

  const isSubscribe =
    subscribeInput instanceof HTMLInputElement && subscribeInput.checked;

  setToggleActiveState(container, isSubscribe);
}

function syncAllSubscribeIndicators() {
  document.querySelectorAll('.cart-line-subscribe').forEach((container) => {
    if (container instanceof HTMLElement) {
      syncToggleStateFromInputs(container);
    }
  });
}

function observeSubscribeIndicators() {
  if (!subscribeIndicatorResizeObserver) {
    subscribeIndicatorResizeObserver = new ResizeObserver(() => {
      syncAllSubscribeIndicators();
    });
  }

  document.querySelectorAll('.cart-line-subscribe__toggle').forEach((toggle) => {
    subscribeIndicatorResizeObserver?.observe(toggle);
  });
}

/**
 * @param {HTMLElement} container
 * @param {boolean} isSubscribe
 */
function setToggleActiveState(container, isSubscribe) {
  const toggle = container.querySelector('.cart-line-subscribe__toggle');
  const optionEls = container.querySelectorAll('.cart-line-subscribe__option');

  if (toggle instanceof HTMLElement) {
    toggle.classList.toggle('is-subscribe-active', isSubscribe);
    toggle.classList.toggle('is-onetime-active', !isSubscribe);
  }

  optionEls.forEach((option) => {
    if (!(option instanceof HTMLElement)) return;

    const isSubscribeOption = option.classList.contains('cart-line-subscribe__option--subscribe');
    option.classList.toggle('is-active', isSubscribe ? isSubscribeOption : !isSubscribeOption);
  });

}

/**
 * @param {Record<string, any>} cartData
 * @param {string} sourceSectionId
 */
function refreshCartFromResponse(cartData, sourceSectionId) {
  const sectionHtml = cartData.sections?.[sourceSectionId];

  if (!sectionHtml) {
    window.location.reload();
    return;
  }

  const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const newCartHiddenItemCount = parsedSection.querySelector('[ref="cartItemCount"]')?.textContent;
  const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (!(component instanceof HTMLElement) || !component.dataset.sectionId) return;

    const sectionId = component.dataset.sectionId;
    const html = cartData.sections?.[sectionId];

    if (!html) return;

    morphSection(sectionId, html, component.dataset.drawer !== undefined ? 'hydration' : 'full');
    resetShimmer(component);
  });

  document.querySelectorAll('cart-quantity-selector-component').forEach((selector) => {
    /** @type {any} */ (selector).updateButtonStates?.();
  });

  scheduleSubscribeUiSync();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      syncSubscribeSavingsAfterMorph();
    });
  });

  document.dispatchEvent(
    new CartUpdateEvent(cartData, sourceSectionId, {
      itemCount: newCartItemCount,
      source: 'cart-subscribe',
      sections: cartData.sections,
    })
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    syncAllSubscribeIndicators();
    observeSubscribeIndicators();
  });
} else {
  syncAllSubscribeIndicators();
  observeSubscribeIndicators();
}

function scheduleSubscribeUiSync() {
  requestAnimationFrame(() => {
    syncAllSubscribeIndicators();
    observeSubscribeIndicators();
    requestAnimationFrame(() => {
      syncAllSubscribeIndicators();
      window.setTimeout(syncAllSubscribeIndicators, 50);
    });
  });
}

/** @param {CartUpdateEvent} event */
function handleCartUpdateForSubscribe(event) {
  scheduleSubscribeUiSync();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      syncAllSubscribeIndicators();

      if (event.detail?.data?.source !== 'cart-subscribe') {
        syncSubscribeSavingsAfterMorph();
      }
    });
  });
}

document.addEventListener(CartUpdateEvent.eventName, handleCartUpdateForSubscribe);

document.addEventListener('dcoded-cart-drawer-refreshed', () => {
  scheduleSubscribeUiSync();
});

document.addEventListener(DialogOpenEvent.eventName, async (event) => {
  if (!(event.target instanceof HTMLElement) || !event.target.closest('cart-drawer-component')) {
    return;
  }

  scheduleSubscribeUiSync();

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      credentials: 'same-origin',
    });

    if (response.ok) {
      const cart = await response.json();
      updateSubscribeSavingsFromCart(cart);
    } else {
      syncSubscribeSavingsAfterMorph();
    }
  } catch (error) {
    console.error('Cart subscribe savings:', error);
    syncSubscribeSavingsAfterMorph();
  }
});

/**
 * @param {HTMLElement} container
 * @param {string} message
 */
function showSubscribeError(container, message) {
  let errorEl = container.querySelector('[data-cart-subscribe-error]');

  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.className = 'cart-line-subscribe__error';
    errorEl.dataset.cartSubscribeError = '';
    container.appendChild(errorEl);
  }

  errorEl.textContent = message;
}

/**
 * @param {HTMLElement} container
 */
function clearSubscribeError(container) {
  container.querySelector('[data-cart-subscribe-error]')?.remove();
}

/**
 * @param {HTMLElement} container
 * @param {HTMLInputElement} selectedInput
 */
async function handleSubscribeToggle(container, selectedInput) {
  if (container.classList.contains('is-loading')) return;

  const fallbackLineIndex = resolveCartLineIndex(container);
  const lineKey = container.dataset.lineKey;
  const sellingPlanId = container.dataset.sellingPlanId;
  const quantity = resolveCartQuantity(container);
  const lineIndex = await resolveLiveCartLine(lineKey, fallbackLineIndex);

  if (!lineIndex) {
    showSubscribeError(container, 'Unable to update this item.');
    return;
  }

  const isSubscribe = selectedInput.value !== '';

  if (isSubscribe && !sellingPlanId) {
    showSubscribeError(container, 'Subscription is not available for this item.');
    return;
  }

  const option = selectedInput.closest('.cart-line-subscribe__option');
  if (option instanceof HTMLElement && option.classList.contains('is-active')) {
    return;
  }

  setToggleActiveState(container, isSubscribe);
  clearSubscribeError(container);

  const cartItemsComponent = container.closest('cart-items-component');
  const sourceSectionId =
    cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;

  cartItemsComponent?.classList.add('cart-items-disabled');
  container.classList.add('is-loading');
  container.querySelectorAll('.cart-subscribe-toggle').forEach((input) => {
    input.disabled = true;
  });

  try {
    const cartData = await changeCartLineSellingPlan(
      lineIndex,
      isSubscribe ? selectedInput.value || null : null,
      quantity,
      lineKey || ''
    );

    if (sourceSectionId) {
      refreshCartFromResponse(cartData, sourceSectionId);
      document.dispatchEvent(new CustomEvent('dcoded-cart-drawer-refreshed'));
    } else {
      window.location.reload();
    }
  } catch (error) {
    console.error(error);
    const message = error instanceof Error ? error.message : 'Unable to update subscription.';
    showSubscribeError(container, message);

    syncToggleStateFromInputs(container);
  } finally {
    cartItemsComponent?.classList.remove('cart-items-disabled');
    container.classList.remove('is-loading');
    container.querySelectorAll('.cart-subscribe-toggle').forEach((input) => {
      input.disabled = false;
    });
  }
}

document.addEventListener('click', (event) => {
  if (
    event.target instanceof Element &&
    event.target.closest('[data-promo-sheet], [data-promo-overlay], dcoded-cart-promo')
  ) {
    return;
  }

  const option = event.target instanceof Element ? event.target.closest('.cart-line-subscribe__option') : null;

  if (!option || option.closest('.cart-line-subscribe.is-loading')) return;

  const container = option.closest('.cart-line-subscribe');
  if (!(container instanceof HTMLElement)) return;

  const input = option.querySelector('.cart-subscribe-toggle');
  if (!(input instanceof HTMLInputElement)) return;

  event.preventDefault();

  container.querySelectorAll('.cart-subscribe-toggle').forEach((toggle) => {
    if (toggle instanceof HTMLInputElement) {
      toggle.checked = toggle === input;
    }
  });

  handleSubscribeToggle(container, input);
});

